import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import InputSearch from 'common/components/inputSearch/InputSearch';
import ListingTable, { ListingTableColumn } from 'common/components/listingTable/ListingTable';
import PaginationWithInfo from 'common/components/pagination/PaginationWithInfo';
import DateFormat from 'common/components/dateFormat/dateFormat';
import PrivacyService from 'api/privacy/PrivacyService';
import { PrivacyHistoryDto } from 'api/privacy/models/PrivacyHistoryDto';
import { DEFAULT_PAGINATION_ITEMS_PER_PAGE } from 'Config';
import styles from '../PrivacyTab.module.scss';

type Props = {
    entityType: 'driver' | 'vehicle';
    refreshKey?: number;
};

const PrivacyHistoryTable: React.FC<Props> = ({ entityType, refreshKey }: Props) => {
    const { t } = useTranslation();
    const [allItems, setAllItems] = useState<PrivacyHistoryDto[]>([]);
    const [search, setSearch] = useState('');
    const [loading, setLoading] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);

    const loadHistory = useCallback(async () => {
        try {
            setLoading(true);
            const fetchFn = entityType === 'driver'
                ? PrivacyService.getDriversHistory
                : PrivacyService.getVehiclesHistory;
            const result = await fetchFn({ page: 1, itemsPerPage: 9999, allIn: search || undefined });
            setAllItems(result.items);
            setCurrentPage(1);
        } catch {
            // Error handled by Http interceptor
        } finally {
            setLoading(false);
        }
    }, [entityType, search, refreshKey]);

    useEffect(() => {
        loadHistory();
    }, [loadHistory]);

    const totalItems = allItems.length;
    const pageItems = allItems.slice((currentPage - 1) * DEFAULT_PAGINATION_ITEMS_PER_PAGE, currentPage * DEFAULT_PAGINATION_ITEMS_PER_PAGE);

    const columns: ListingTableColumn<PrivacyHistoryDto>[] = [
        {
            field: 'entityName',
            name: entityType === 'driver'
                ? t('privacy.history_col_driver') as string
                : t('privacy.history_col_vehicle') as string,
            renderCell: row => <><strong>{row.entityName}</strong></>,
        },
        {
            field: 'entityType',
            name: t('privacy.history_col_type') as string,
            renderCell: row => <>{row.entityType === 'DRIVER' ? t('privacy.driver') as string : t('privacy.vehicle') as string}</>,
        },
        {
            field: 'previousState',
            name: t('privacy.history_col_previous_state') as string,
            renderCell: row => (
                <span className={row.previousState ? styles.stateOn : styles.stateOff}>
                    {row.previousState ? 'ON' : 'OFF'}
                </span>
            ),
        },
        {
            field: 'newState',
            name: t('privacy.history_col_new_state') as string,
            renderCell: row => (
                <span className={row.newState ? styles.stateOn : styles.stateOff}>
                    {row.newState ? 'ON' : 'OFF'}
                </span>
            ),
        },
        {
            field: 'changedByUserName',
            name: t('privacy.history_col_changed_by') as string,
            renderCell: row => <>{row.changedByUserName || '-'}</>,
        },
        {
            field: 'changedDate',
            name: t('privacy.history_col_date') as string,
            renderCell: row => <><DateFormat value={row.changedDate} format="DD/MM/YYYY | HH:mm" /></>,
        },
    ];

    const titleKey = entityType === 'driver' ? 'privacy.drivers_history_title' : 'privacy.vehicles_history_title';
    const descKey = entityType === 'driver' ? 'privacy.drivers_history_desc' : 'privacy.vehicles_history_desc';
    const searchPlaceholderKey = entityType === 'driver' ? 'privacy.search_driver' : 'privacy.search_vehicle';

    return (
        <div className={styles.card}>
            <div className={styles.cardHeader}>
                <div>
                    <div className={styles.cardTitle}>{t(titleKey) as string}</div>
                    <div className={styles.cardDesc}>{t(descKey) as string}</div>
                </div>
                <InputSearch onChangeValue={val => setSearch(val)} placeholder={t(searchPlaceholderKey) as string} />
            </div>
            <div className={styles.cardBodyNoPadding}>
                <ListingTable
                    columns={columns}
                    rows={pageItems}
                    allowHover
                />
                <PaginationWithInfo
                    itemName=""
                    currentPage={currentPage}
                    pageItems={pageItems.length}
                    totalItems={totalItems}
                    onChange={setCurrentPage}
                />
            </div>
        </div>
    );
};

export default PrivacyHistoryTable;
